import React from "react";
import { addCartItem } from "actions/cart";
import { useDispatch, useSelector } from "react-redux";
import { updateCartItem } from "actions/cart";
import { ADD_TO_CART, REMOVE_FROM_CART, UPDATE_ITEM, OPEN_CART } from "utils/action-types";
import { trackAddToCart, trackRemoveFromCart } from "services/analytics";
import { isMobile } from "utils/device-detect";
import { useGlobalContext } from "context/global";
import clsx from "clsx/lite";
import extractIdFromGid from "helpers/extract-gid";
import { freeGiftRemovedKey } from "utils/constants";

const QuantityInput = ({
	product, openCart = false, customButtonClass = "", isInCart, newLayout = false, isInModal = false, onRemoved
}) => {
	const cart = useSelector((state) => state.cart);
	const isMobileView = isMobile();
	const { isCollection, cartSettings } = useGlobalContext();
	const shouldNotOpenCart = (isCollection && isMobileView) || isInModal;
	const dispatch = useDispatch();

	if (!product.variant) return null;

	const { variant, title = "", image = null } = product;

	const cartItem = cart?.items[variant.id];

	const isFreeGift = cartSettings.bonus?.product && variant.id === cartSettings.bonus.product.variant.id;

	const weight = variant.weight === 0.001 ? 0 : variant.weight;

	const addProduct = () => {
		if (isFreeGift) return;

		dispatch({
			type: ADD_TO_CART, payload: {
				id: variant.id, totalPrice: variant.price, title, image: { src: image, altText: title },
				quantity: 1, weight, shouldNotOpenCart
			}
		});

		dispatch(addCartItem(variant.id, variant.stock === "one-time"));
		trackAddToCart({ ...product, quantity: 1 });
		if (openCart)
			dispatch({ type: OPEN_CART });
	};

	const updateQuantity = (qty) => {
		const isIncreasing = qty > 0;
		if (isFreeGift && isIncreasing) return;

		const quantity = cartItem.quantity + qty;
		const totalQuantity = cart?.totalQuantity + qty;
		const totalProgress = cart?.totalProgress + (isIncreasing ? weight : -weight);
		if (quantity === 0) {
			dispatch({ type: REMOVE_FROM_CART, id: variant.id, weight });
			if (isFreeGift) 
				sessionStorage.setItem(freeGiftRemovedKey, product.variant.id);

			onRemoved?.();
		}
		else
			dispatch({ type: UPDATE_ITEM, id: variant.id, quantity, totalQuantity, totalProgress, shouldNotOpenCart });

		dispatch(updateCartItem(cartItem?.tempId, quantity));

		// track event
		isIncreasing ?
			trackAddToCart({ ...product, quantity: 1 }) :
			trackRemoveFromCart({ ...product, quantity: 1 });
	};

	const showNewLayout = isCollection || newLayout;

	const buttonClass = clsx(
		"plus-minus-button",
		!showNewLayout && !isInCart && "!size-11 md:!w-14 md:!h-8 !rounded-[37px]",
		showNewLayout && "!w-3.5",
		customButtonClass
	);

	const gid = extractIdFromGid(variant?.id);

	const AddToCartButton = () => {
		if (isFreeGift) return null;

		return (
			<button onClick={addProduct}
				id={`add-to-cart-${gid}`}
				className={clsx(showNewLayout ?
					"bg-beige rounded-lg md:w-full w-10/12 py-1 text-h3/5 md:text-3xl/5 mt-4" :
					"plus-minus-button bg-plus-btn",
				)}
				disabled={cart.updating}
				aria-label="Quantity"
			>
				{showNewLayout ? "+" : ""}
			</button>
		);
	};

	const QuantityControlButtons = () => (
		<div className={clsx("flex gap-4 items-center", !isInCart && "mt-4", variant.weight <= 1 && "justify-start", 
			showNewLayout && !isInCart && "bg-brown w-10/12 md:w-full justify-between px-6 rounded-lg")}>
			<button
				id={`minus-action-${gid}`}
				aria-label="Minus"
				disabled={cart.updating}
				className={clsx(buttonClass, showNewLayout && !isInCart ? "bg-minus" : "bg-minus-grey")}
				onClick={() => updateQuantity(-1)} />
			<p className={clsx("text-base", showNewLayout && !isInCart ? "text-white" : "text-secondary")}>
				{cartItem?.quantity}
			</p>
			<button
				id={`plus-action-${gid}`}
				aria-label="Plus"
				disabled={cart.updating}
				className={clsx(
					buttonClass, !isInCart 
						? showNewLayout ? "bg-plus" : "bg-plus-black" 
						: "bg-plus-grey",
					isFreeGift && "opacity-20 pointer-events-none"
				)}
				onClick={() => updateQuantity(1)} />
		</div>
	);


	if (!cart.checkoutUrl) return <div className="h-[173px]" />;

	return (
		<div className="flex md:m-auto md:justify-center items-center">
			{!cartItem?.id ? <AddToCartButton /> : <QuantityControlButtons />}
		</div>
	);
};

export default QuantityInput;
